import { Pill, SlidersHorizontal, TriangleAlert } from 'lucide-react';
import { useState } from 'react';

import type { ErroResponse, MedicamentoDoTratamentoResponse } from '@/api/generated/model';
import { usePacientesControllerObter } from '@/api/generated/pacientes/pacientes';
import {
  useTratamentosControllerListar,
  useTratamentosControllerObter,
} from '@/api/generated/tratamentos/tratamentos';
import { AjusteDeDoseDialogo } from '@/features/historico/components/AjusteDeDoseDialogo';
import {
  AVISO_NAO_PADRONIZADO,
  descreverDose,
  descreverDoseAbsoluta,
  descreverFrequencia,
  formatarDia,
  formatarPeriodo,
  ROTULO_VIA,
} from '@/features/historico/tratamento';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Paginacao } from '@/shared/ui/pagination';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';

const POR_PAGINA = 10;

/**
 * Aba Tratamentos (Fase 1f).
 *
 * Leitura pura, com uma exceção: o ajuste de dose avulso — o que se registra
 * fora da consulta. Tratamento **entra** pelo prontuário; esta aba é onde ele
 * se lê.
 */
export function AbaTratamentos({ pacienteId }: { pacienteId: string }) {
  const [pagina, setPagina] = useState(1);

  const lista = useTratamentosControllerListar({
    pacienteId,
    pagina,
    porPagina: POR_PAGINA,
    ordenarPor: 'iniciadoEm',
    // Mais recente primeiro: quem abre a aba quer o tratamento em curso, não o
    // de dois anos atrás.
    ordem: 'desc',
  });

  const paciente = usePacientesControllerObter(pacienteId);
  const pesoKg = paciente.data?.pesoKg ?? null;

  if (lista.isPending) {
    return (
      <div className="space-y-3">
        {[0, 1].map((i) => (
          <Skeleton key={i} className="h-40 rounded-lg" />
        ))}
      </div>
    );
  }

  if (lista.isError || !lista.data) {
    return (
      <div className="bg-surface-raised rounded-lg border-2 border-brand/20">
        <EstadoErro descricao={descreverFalha(lista.error)} aoTentarDeNovo={() => void lista.refetch()} />
      </div>
    );
  }

  if (lista.data.itens.length === 0) {
    return (
      <div className="bg-surface-raised rounded-lg border-2 border-brand/20">
        <EstadoVazio
          icone={Pill}
          titulo="Nenhum tratamento registrado"
          descricao="O tratamento entra pelo prontuário: abra um atendimento e marque “Em tratamento”."
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {lista.data.itens.map((resumo) => (
        <CardDeTratamento
          key={resumo.id}
          tratamentoId={resumo.id}
          pacienteId={pacienteId}
          pesoKg={pesoKg}
        />
      ))}

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20">
        <Paginacao
          pagina={pagina}
          totalPaginas={lista.data.totalPaginas}
          total={lista.data.total}
          porPagina={POR_PAGINA}
          aoMudarPagina={setPagina}
        />
      </div>
    </div>
  );
}

interface PropsDoCard {
  tratamentoId: string;
  pacienteId: string;
  pesoKg: number | null;
}

function CardDeTratamento({ tratamentoId, pacienteId, pesoKg }: PropsDoCard) {
  const [aAjustar, setAAjustar] = useState<MedicamentoDoTratamentoResponse | null>(null);
  const { pode } = usePermissoes();

  // O resumo da lista não traz vigências, e é a tabela de vigências que faz
  // esta aba existir.
  const tratamento = useTratamentosControllerObter(tratamentoId);

  if (tratamento.isPending) return <Skeleton className="h-40 rounded-lg" />;

  if (tratamento.isError || !tratamento.data) {
    return (
      <div className="bg-surface-raised rounded-lg border-2 border-brand/20">
        <EstadoErro aoTentarDeNovo={() => void tratamento.refetch()} />
      </div>
    );
  }

  const dados = tratamento.data;
  const podeAjustar = pode('tratamentos.editar') && dados.emUso;

  return (
    <section className="bg-surface-raised rounded-lg border-2 border-brand/20 p-6 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-foreground">{dados.titulo}</h3>
          <p className="text-sm text-muted-foreground">
            {[
              `Início em ${formatarDia(dados.iniciadoEm)}`,
              dados.encerradoEm ? `Encerrado em ${formatarDia(dados.encerradoEm)}` : null,
              dados.diagnosticoNome,
              dados.veterinarioNome,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>

        <Badge variant="outline" className={dados.emUso ? 'text-emerald-600 border-emerald-600/40' : ''}>
          {dados.emUso ? 'Em curso' : 'Encerrado'}
        </Badge>
      </div>

      {dados.observacoes && (
        <p className="text-sm text-muted-foreground whitespace-pre-line">{dados.observacoes}</p>
      )}

      {dados.medicamentos.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum fármaco neste tratamento.</p>
      ) : (
        <ul className="space-y-5">
          {dados.medicamentos.map((medicamento) => (
            <li key={medicamento.id} className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-foreground">{medicamento.nome}</span>

                  {medicamento.principioAtivo &&
                    medicamento.principioAtivo !== medicamento.nome && (
                      <span className="text-xs text-muted-foreground">
                        {medicamento.principioAtivo}
                      </span>
                    )}

                  {!medicamento.padronizado && (
                    <Badge variant="outline" className="text-amber-600 border-amber-600/40">
                      Fora do catálogo
                    </Badge>
                  )}

                  {medicamento.encerradoEm && (
                    <Badge variant="outline">
                      Retirado em {formatarDia(medicamento.encerradoEm)}
                    </Badge>
                  )}
                </div>

                {podeAjustar && !medicamento.encerradoEm && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setAAjustar(medicamento)}
                  >
                    <SlidersHorizontal className="h-4 w-4" />
                    Ajustar dose
                  </Button>
                )}
              </div>

              {/* 🔴 A tabela que PROVA que o histórico não se sobrescreve: depois
                  de um ajuste, a primeira linha continua ali, com o período dela
                  fechado. */}
              {!medicamento.padronizado && (
                <p className="flex items-start gap-1.5 text-xs text-amber-600">
                  <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  {AVISO_NAO_PADRONIZADO}
                </p>
              )}

              {medicamento.vigencias.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhuma dose registrada.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Período</TableHead>
                      <TableHead>{medicamento.nome}</TableHead>
                      <TableHead>Frequência</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {medicamento.vigencias.map((vigencia) => (
                      <TableRow key={vigencia.id}>
                        <TableCell className="whitespace-nowrap tabular-nums">
                          {formatarPeriodo(vigencia.vigenteDe, vigencia.vigenteAte)}
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{descreverDose(vigencia)}</span>
                          {vigencia.dosePorKg !== null && (
                            <span className="text-muted-foreground">
                              {' '}
                              ({descreverDoseAbsoluta(vigencia)}
                              {vigencia.pesoKgReferencia !== null &&
                                ` com ${vigencia.pesoKgReferencia} kg`}
                              )
                            </span>
                          )}
                          {vigencia.via && (
                            <span className="block text-xs text-muted-foreground">
                              {ROTULO_VIA[vigencia.via] ?? vigencia.via}
                              {vigencia.apresentacaoNome
                                ? ` · ${vigencia.apresentacaoNome}`
                                : vigencia.nomeComercialLivre
                                  ? ` · ${vigencia.nomeComercialLivre}`
                                  : ''}
                            </span>
                          )}
                          {vigencia.motivo && (
                            <span className="block text-xs text-muted-foreground">
                              {vigencia.motivo}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {descreverFrequencia(vigencia.frequencia, vigencia.frequenciaLivre)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </li>
          ))}
        </ul>
      )}

      {aAjustar && (
        <AjusteDeDoseDialogo
          aberto
          aoFechar={() => setAAjustar(null)}
          tratamentoId={dados.id}
          pacienteId={pacienteId}
          medicamento={aAjustar}
          pesoKg={pesoKg}
        />
      )}
    </section>
  );
}

/**
 * A mensagem do servidor, quando ela existe.
 *
 * ⚠️ "Algo deu errado. Tente novamente." é a descrição errada para um **403**:
 * tentar de novo não resolve nada, e o usuário fica clicando. O caso que motivou
 * isto é concreto — quando as permissões `tratamentos.*` ainda não foram
 * semeadas no banco, TODA rota deste módulo responde 403, e a aba inteira dizia
 * apenas "algo deu errado", sem pista de que o problema é o `db:seed` do deploy.
 *
 * `undefined` deixa o `EstadoErro` usar o texto padrão, que continua certo para
 * falha de rede.
 */
function descreverFalha(erro: ErroResponse | null): string | undefined {
  return erro?.erro?.mensagem;
}
